/**
 * Scroll-scrubbed <video> — no canvas.
 * currentTime mapped from ScrollTrigger progress; updates rAF-coalesced.
 * Required attrs: playsinline, muted, preload="metadata", poster.
 */

export const REQUIRED_VIDEO_ATTRS = {
  playsinline: true,
  muted: true,
  preload: 'metadata',
} as const;

export interface ScrubberOptions {
  /** Poster URL applied as attribute. */
  poster?: string;
  /** Duration override when metadata not yet available. */
  durationHint?: number;
  /** Inject rAF/cAF for tests. */
  raf?: (cb: FrameRequestCallback) => number;
  caf?: (id: number) => void;
}

/**
 * Apply the scrubber attribute contract to a video element.
 * Covered by acceptance tests.
 */
export function applyScrubAttrs(
  video: HTMLVideoElement,
  poster?: string,
): void {
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.muted = true;
  video.defaultMuted = true;
  video.setAttribute('muted', '');
  video.preload = 'metadata';
  video.setAttribute('preload', 'metadata');
  // Never autoplay — scrub only
  video.autoplay = false;
  video.loop = false;
  video.controls = false;
  if (poster) {
    video.poster = poster;
    video.setAttribute('poster', poster);
  }
}

/**
 * Map scroll progress [0,1] → media time.
 * Clamps to [0, duration). Uses duration - epsilon so the last frame holds.
 */
export function progressToTime(progress: number, duration: number): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  const p = clamp01(progress);
  // Stay just shy of duration so currentTime is always a valid frame
  const epsilon = 1 / 120;
  const maxT = Math.max(0, duration - epsilon);
  return p * maxT;
}

export function clamp01(n: number): number {
  if (n <= 0) return 0;
  if (n >= 1) return 1;
  return n;
}

/**
 * Verify attribute contract (for tests + runtime assert).
 */
export function hasRequiredAttrs(video: HTMLVideoElement): boolean {
  const playsinline =
    video.playsInline === true ||
    video.hasAttribute('playsinline') ||
    video.getAttribute('playsinline') === '';
  const muted = video.muted === true;
  const preload = video.preload === 'metadata';
  return playsinline && muted && preload;
}

export class Scrubber {
  readonly video: HTMLVideoElement;
  private pendingProgress: number | null = null;
  private rafId: number | null = null;
  private durationHint: number;
  private readonly raf: (cb: FrameRequestCallback) => number;
  private readonly caf: (id: number) => void;
  private lastApplied = -1;
  private disposed = false;

  /** Seek metrics: assign→seeked durations (ms), first load excluded by caller. */
  readonly seekSamplesMs: number[] = [];
  private measuringSeek = false;
  private seekStart = 0;
  private skipNextSeekMetric = true;

  constructor(video: HTMLVideoElement, options: ScrubberOptions = {}) {
    this.video = video;
    this.durationHint = options.durationHint ?? 0;
    this.raf =
      options.raf ??
      ((cb) =>
        typeof requestAnimationFrame !== 'undefined'
          ? requestAnimationFrame(cb)
          : (setTimeout(() => cb(performance.now()), 16) as unknown as number));
    this.caf =
      options.caf ??
      ((id) => {
        if (typeof cancelAnimationFrame !== 'undefined') {
          cancelAnimationFrame(id);
        } else {
          clearTimeout(id);
        }
      });

    applyScrubAttrs(video, options.poster);

    video.addEventListener('seeked', this.onSeeked);
    video.addEventListener('loadedmetadata', this.onMeta);
  }

  private onMeta = (): void => {
    if (this.video.duration && Number.isFinite(this.video.duration)) {
      this.durationHint = this.video.duration;
    }
  };

  private onSeeked = (): void => {
    if (!this.measuringSeek) return;
    this.measuringSeek = false;
    if (this.skipNextSeekMetric) {
      this.skipNextSeekMetric = false;
      return;
    }
    const dt = performance.now() - this.seekStart;
    if (Number.isFinite(dt) && dt >= 0) {
      this.seekSamplesMs.push(dt);
    }
  };

  getDuration(): number {
    const d = this.video.duration;
    if (Number.isFinite(d) && d > 0) return d;
    return this.durationHint;
  }

  /**
   * Queue a progress update; applied once per animation frame.
   */
  setProgress(progress: number): void {
    if (this.disposed) return;
    this.pendingProgress = clamp01(progress);
    if (this.rafId === null) {
      this.rafId = this.raf(() => {
        this.rafId = null;
        this.flush();
      });
    }
  }

  /** Immediate apply (tests / reduced-motion). */
  applyProgressNow(progress: number): void {
    this.pendingProgress = clamp01(progress);
    this.flush();
  }

  private flush(): void {
    if (this.disposed || this.pendingProgress === null) return;
    const progress = this.pendingProgress;
    this.pendingProgress = null;
    const duration = this.getDuration();
    const t = progressToTime(progress, duration);
    if (Math.abs(t - this.lastApplied) < 1e-4) return;
    this.lastApplied = t;

    try {
      this.measuringSeek = true;
      this.seekStart = performance.now();
      this.video.currentTime = t;
    } catch {
      // Some browsers throw if metadata not ready; ignore.
      this.measuringSeek = false;
    }
  }

  /** p95 of recorded seek samples (ms). */
  seekP95(): number | null {
    return percentile(this.seekSamplesMs, 0.95);
  }

  dispose(): void {
    this.disposed = true;
    if (this.rafId !== null) {
      this.caf(this.rafId);
      this.rafId = null;
    }
    this.video.removeEventListener('seeked', this.onSeeked);
    this.video.removeEventListener('loadedmetadata', this.onMeta);
  }
}

export function percentile(samples: number[], p: number): number | null {
  if (!samples.length) return null;
  const sorted = [...samples].sort((a, b) => a - b);
  const idx = Math.min(
    sorted.length - 1,
    Math.max(0, Math.ceil(p * sorted.length) - 1),
  );
  return sorted[idx] ?? null;
}
